import React from 'react';

import { Slider, Button, Space, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { Play, Pause, RotateCcw, Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  getMapDesktopBottomStackCss,
  MAP_OVERLAY_Z,
} from '@/features/map/constants/mapOverlayLayout';
import { getGlobalPeakModelHourForCalendarDay } from '@/features/map/utils/floodModelDiurnalPeak';
import {
  getFirstSelectableFloodTimelineDayIsoForYear,
  isFloodTimelineDayWithData,
  isFloodTimelineSelectableDay,
} from '@/features/map/utils/floodTimelineCalendar';
import { buildTimeSliderDates } from '@/features/map/utils/timeSliderDates';
import { setTimeSliderHistoryDay } from '@/features/scenario/stores/scenarioSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { cn } from '@/utils/cn';

interface TimeSliderProps {
  year: number;
  currentDate: string | null;
  onChange: (date: string) => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  onReset: () => void;
  /** `stacked` = inside mobile bottom column (full width); `floating` = centered bar (desktop). */
  layout?: 'floating' | 'stacked';
}

export const TimeSlider: React.FC<TimeSliderProps> = ({
  year,
  currentDate,
  onChange,
  isPlaying,
  onTogglePlay,
  onReset,
  layout = 'floating',
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const historyDay = useAppSelector((s) => s.scenario.timeSliderHistoryDay);
  const isModelInfoVisible = useAppSelector((s) => {
    // Hiện thông tin đỉnh mô hình nếu bật lớp ngập lụt hoặc các lớp dự báo/vệ tinh có yếu tố mô hình
    const visibleIds = s.layer.layers.filter((l) => l.visible).map((l) => l.id);
    return visibleIds.some(
      (id) => id === 'flood' || id.includes('forecast') || id.includes('simulation'),
    );
  });

  const selectedDay = React.useMemo(() => dayjs(historyDay), [historyDay]);

  const modelPeakHour = React.useMemo(
    () => getGlobalPeakModelHourForCalendarDay(historyDay),
    [historyDay],
  );

  /** Khớp năm lịch với năm phân tích (dropdown trên) và chỉ giữ ngày trong mùa có dữ liệu. */
  React.useEffect(() => {
    const d = dayjs(historyDay);
    if (d.year() !== year || !isFloodTimelineSelectableDay(d, year)) {
      dispatch(setTimeSliderHistoryDay(getFirstSelectableFloodTimelineDayIsoForYear(year)));
    }
  }, [year, historyDay, dispatch]);

  const dates = React.useMemo(() => buildTimeSliderDates(historyDay), [historyDay]);

  /** Mốc giờ dưới thanh (0–23 trùng chỉ số slider). */
  const hourMarks = React.useMemo(() => {
    const m: Record<number, React.ReactNode> = {};
    const label = (h: number) => (
      <span className="inline-block min-w-[1.5em] text-center text-[10px] font-semibold tabular-nums text-slate-400">
        {h}h
      </span>
    );
    for (let h = 0; h < 24; h += 3) {
      m[h] = label(h);
    }
    m[23] = label(23);
    return m;
  }, []);

  const currentIndex = currentDate ? dates.indexOf(currentDate) : 0;

  React.useEffect(() => {
    if (dates.length > 0 && (!currentDate || !dates.includes(currentDate))) {
      onChange(dates[0]);
    }
  }, [dates, currentDate, onChange]);

  const desktopStack = getMapDesktopBottomStackCss();

  const shellClass =
    layout === 'stacked'
      ? 'relative w-full rounded-2xl border border-[var(--glass-border)] bg-[var(--map-chrome-panel)] p-3 shadow-2xl backdrop-blur-xl sm:p-4'
      : 'absolute left-1/2 w-[min(650px,calc(100vw-48px))] -translate-x-1/2 rounded-2xl border border-[var(--glass-border)] bg-[var(--map-chrome-panel)] p-4 shadow-2xl backdrop-blur-xl';

  const shellStyle =
    layout === 'stacked'
      ? { zIndex: MAP_OVERLAY_Z.tooltip }
      : { zIndex: MAP_OVERLAY_Z.tooltip, bottom: desktopStack.timeSliderBottom };

  return (
    <div className={shellClass} style={shellStyle}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Space size="middle" wrap>
            <Button
              type="text"
              icon={isPlaying ? <Pause size={18} /> : <Play size={18} />}
              onClick={onTogglePlay}
              className="text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
            />
            <Button
              type="text"
              icon={<RotateCcw size={18} />}
              onClick={onReset}
              className="text-slate-400 hover:text-slate-300"
            />
            <span title={currentDate ? dayjs(currentDate).format('DD/MM/YYYY HH:mm') : undefined}>
              <DatePicker
                value={selectedDay}
                onChange={(val) => {
                  if (val && isFloodTimelineSelectableDay(val, year)) {
                    dispatch(setTimeSliderHistoryDay(val.format('YYYY-MM-DD')));
                  }
                }}
                allowClear={false}
                /** Chỉ hiển thị tháng/ngày; năm trùng với năm phân tích trên bản đồ. */
                format="DD/MM"
                disabledDate={(d) => !d || !isFloodTimelineSelectableDay(d, year)}
                defaultPickerValue={dayjs(`${year}-06-01`)}
                style={{
                  background: 'var(--form-field-bg)',
                  border: '1px solid var(--form-field-border)',
                  color: 'var(--text-primary)',
                }}
                suffixIcon={<CalendarIcon size={14} color="#94a3b8" />}
                cellRender={(current, info) => {
                  if (info.type !== 'date') return info.originNode;
                  const d = dayjs(current);
                  if (!d.isValid()) return info.originNode;
                  const hasData = isFloodTimelineDayWithData(d, year);
                  return (
                    <div
                      className={cn(
                        'ant-picker-cell-inner',
                        hasData &&
                          'rounded-md bg-sky-500/35 text-white shadow-[inset_0_0_0_1px_rgba(56,189,248,0.65)]',
                      )}
                    >
                      {d.date()}
                    </div>
                  );
                }}
              />
            </span>
          </Space>
        </div>

        <Slider
          min={0}
          max={Math.max(0, dates.length - 1)}
          value={currentIndex === -1 ? 0 : currentIndex}
          onChange={(val) => onChange(dates[val])}
          marks={dates.length > 0 ? hourMarks : undefined}
          tooltip={{
            formatter: (val) => (val !== undefined ? `${new Date(dates[val]).getHours()}h` : ''),
          }}
          className="time-slider-custom"
        />
        {dates.length > 0 && isModelInfoVisible ? (
          <p
            className="m-0 text-[10px] leading-snug text-slate-500"
            title={t('timeSlider.modelPeakFactorDetail')}
          >
            {t('timeSlider.modelPeakFactorLine', { hour: modelPeakHour })}
          </p>
        ) : null}
      </div>

      <style>{`
        .time-slider-custom .ant-slider-rail { background: var(--slider-rail-bg) !important; }
        .time-slider-custom .ant-slider-track { background: #38bdf8 !important; }
        .time-slider-custom .ant-slider-handle::after { background: #38bdf8 !important; box-shadow: 0 0 10px #38bdf8 !important; }
        .time-slider-custom .ant-slider-mark { top: 12px !important; }
        .time-slider-custom .ant-slider-mark-text { white-space: nowrap; }
        .time-slider-custom.ant-slider-with-marks { margin-bottom: 28px; }
      `}</style>
    </div>
  );
};
